
import React, { useState } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Icon } from '@/components/ui/icon'
import { toast } from '@/components/ui/toast'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { CreatedItem } from '../../types'
import { ContentCard, EditingControls } from '../ui'

export interface CreatedItemCardProps {
  item: CreatedItem
  selected?: boolean
  onSelect?: () => void
  onDuplicate?: () => void
  onDragOut?: () => void
  onEdit?: (newContent?: string) => void
  onDelete?: () => void
  onView?: () => void
  locked?: boolean
}

const CreatedItemCardComponent: React.FC<CreatedItemCardProps> = ({ item, selected = false, onSelect, onDuplicate, onDragOut, onEdit, onDelete, onView, locked = false }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [editContent, setEditContent] = useState(
    typeof item.content === 'string' ? item.content : ''
  )

  const handleSaveEdit = () => {
    if (editContent.trim() !== '') {
      onEdit?.(editContent)
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditContent(typeof item.content === 'string' ? item.content : '')
    setIsEditing(false)
  }

  const handleEditClick = () => {
    if (item.type === 'text') {
      setIsEditing(true)
    } else {
      onEdit?.()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect?.()
    }
  }

  return (
    <div
      tabIndex={0}
      role="button"
      aria-label={`${item.type === 'image' ? 'Image' : item.type === 'text' ? 'Text' : item.type.charAt(0).toUpperCase() + item.type.slice(1)} content item`}
      aria-selected={selected}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleKeyDown}
    >
      <ContentCard
        item={item}
        selected={selected}
        onSelect={onSelect}
        onDragOut={onDragOut}
      >
        {/* Editing overlay for text content */}
        {isEditing && item.type === 'text' && (
          <div className="absolute inset-0 flex flex-col bg-card p-2">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              spellCheck={false}
              className="flex-1 resize-none border-0 bg-transparent p-1 text-xs shadow-none focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveEdit()
                }
                if (e.key === 'Escape') {
                  handleCancelEdit()
                }
              }}
              autoFocus
            />
            <EditingControls
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
          </div>
        )}

        {/* Action buttons overlay */}
        {!isEditing && !locked && (
            <div
              className={`absolute right-2 bottom-2 flex gap-1 transition-opacity ${
                selected || isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {item.type === 'text' && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditClick()
                        }}
                      >
                        <Icon name="edit" className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent size="sm">Edit</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label="Copy text"
                        onClick={async (e) => {
                          e.stopPropagation()
                          try {
                            const textContent = typeof item.content === 'string' ? item.content : ''
                            await navigator.clipboard.writeText(textContent)
                            toast.success('Text copied to clipboard')
                          } catch (err) {
                            console.error('Failed to copy text:', err)
                            toast.error('Failed to copy text')
                          }
                        }}
                      >
                        <Icon name="copy" className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent size="sm">Copy Text</TooltipContent>
                  </Tooltip>
                </>
              )}
              {item.type === 'image' && (
                <>
                  {onView && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label="View image"
                          onClick={(e) => {
                            e.stopPropagation()
                            onView()
                          }}
                        >
                          <Icon name="eye" className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent size="sm">View</TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label="Download image"
                        onClick={async (e) => {
                          e.stopPropagation()
                          try {
                            // ✅ Download original version (high quality), not preview
                            const originalUrl = item.metadata?.versions?.original?.url
                            const imageUrl = originalUrl || (typeof item.content === 'string' ? item.content : '')
                            if (!imageUrl) return

                            // Fetch image and download
                            const response = await fetch(imageUrl)
                            const blob = await response.blob()
                            const url = window.URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = item.metadata?.name || `image-${item.id}.png`
                            document.body.appendChild(a)
                            a.click()
                            document.body.removeChild(a)
                            window.URL.revokeObjectURL(url)

                            toast.success('Image downloaded')
                          } catch (err) {
                            console.error('Failed to download image:', err)
                            toast.error('Failed to download image')
                          }
                        }}
                      >
                        <Icon name="download" className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent size="sm">Download</TooltipContent>
                  </Tooltip>
                </>
              )}
              {item.type === 'video' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Download video"
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          const videoUrl = typeof item.content === 'string' ? item.content : ''
                          if (!videoUrl) return

                          const response = await fetch(videoUrl)
                          const blob = await response.blob()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = item.metadata?.name || `video-${item.id}.mp4`
                          document.body.appendChild(a)
                          a.click()
                          document.body.removeChild(a)
                          window.URL.revokeObjectURL(url)

                          toast.success('Video downloaded')
                        } catch (err) {
                          console.error('Failed to download video:', err)
                          toast.error('Failed to download video')
                        }
                      }}
                    >
                      <Icon name="download" className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent size="sm">Download</TooltipContent>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-black/50 p-0 text-white hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Remove"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete()
                      }}
                    >
                      <Icon name="trash" className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent size="sm">Remove</TooltipContent>
                </Tooltip>
              )}
            </div>
        )}
      </ContentCard>
    </div>
  )
}

export const CreatedItemCard = React.memo(CreatedItemCardComponent, (prev, next) => {
  return (
    prev.item.id === next.item.id &&
    prev.item.content === next.item.content &&
    prev.selected === next.selected &&
    prev.locked === next.locked
  )
})
